import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useSettings } from "../lib/hooks";

const QUICK_CHOICES = [
  { key: "extension", label: "I'm planning an extension", msg: "Hello NOOR Design & Build. I'm planning an extension and would like to discuss it with you." },
  { key: "renovation", label: "Renovation or conversion", msg: "Hello NOOR Design & Build. I'm considering a renovation or conversion and would like to talk it through." },
  { key: "newbuild", label: "A new build project", msg: "Hello NOOR Design & Build. I'm planning a new build and would like to discuss it with you." },
  { key: "question", label: "Just a quick question", msg: "Hello NOOR Design & Build. I have a quick question about a possible project." },
];

const waNumber = (settings) => `44${(settings?.whatsapp || "07782760279").replace(/^0/, "")}`;

function WhatsAppIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export default function WhatsAppBubble() {
  const { data: settings } = useSettings();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  const openWhatsApp = (msg) => {
    window.open(`https://wa.me/${waNumber(settings)}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  // Desktop only — mobile already has the sticky CALL / WHATSAPP / CONSULT action bar.
  return (
    <div ref={ref} className="hidden md:block fixed bottom-8 right-8 z-40" data-testid="whatsapp-bubble">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.25 }}
            className="absolute bottom-16 right-0 w-72 bg-graphite text-white border border-white/15 shadow-2xl"
            data-testid="whatsapp-bubble-panel"
          >
            <div className="p-5 border-b border-white/10">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ember">Chat with NOOR</p>
              <p className="text-sm text-white/70 mt-2 leading-relaxed">
                Message us on WhatsApp — we usually reply the same day.
              </p>
            </div>
            <div className="p-2">
              {QUICK_CHOICES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => openWhatsApp(c.msg)}
                  data-testid={`whatsapp-choice-${c.key}`}
                  className="w-full text-left px-4 py-3 text-sm text-white/80 hover:bg-white/5 hover:text-ember transition-colors duration-200"
                >
                  {c.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close WhatsApp chat" : "Chat with us on WhatsApp"}
        aria-expanded={open}
        data-testid="whatsapp-bubble-toggle"
        className="ml-auto flex w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1fb857] text-white items-center justify-center shadow-xl transition-all duration-300 hover:scale-105"
      >
        {open ? <X size={22} /> : <WhatsAppIcon size={26} />}
      </button>
    </div>
  );
}
